import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useArticles } from "../../contexts/ArticlesContext";
import { FiClock, FiUser, FiX, FiBookOpen, FiArrowLeft } from "react-icons/fi";
import { Link } from "react-router-dom";

function DetailsArticlePage() {
  const { id } = useParams();
  const { articles } = useArticles();
  const article = articles.find((article) => article.id === id);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!article) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 px-4">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Article not found
          </h2>
          <p className="text-gray-600 mb-6">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/articles"
            className="text-indigo-600 hover:text-indigo-800 flex items-center justify-center"
          >
            <FiArrowLeft className="mr-2" /> Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  // Convert ISO date string to human-readable format
  const formattedDate = new Date(article.createdAt).toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div className="bg-white min-h-screen">
      {/* Header Image with Title Overlay */}
      <div className="relative h-96 bg-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900 z-10"></div>
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover opacity-80"
          onClick={() => setIsModalOpen(true)}
        />
        <div className="absolute bottom-0 left-0 w-full p-6 z-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-white">
              <p className="uppercase tracking-wider text-indigo-300 mb-2 font-medium text-sm">
                {article.category}
              </p>
              <h1 className="text-4xl font-bold mb-3">{article.title}</h1>
              <div className="flex items-center text-gray-300 text-sm mb-6 flex-wrap">
                <div className="flex items-center mr-6 mb-2">
                  <FiUser className="mr-2" />
                  <span>{article.authorName}</span>
                </div>
                <div className="flex items-center mb-2">
                  <FiClock className="mr-2" />
                  <span>{formattedDate}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link
          to="/articles"
          className="text-indigo-600 hover:text-indigo-800 flex items-center mb-8"
        >
          <FiArrowLeft className="mr-2" /> Back to Articles
        </Link>

        {/* Article Body */}
        <div className="prose prose-lg max-w-none mb-12">
          {JSON.parse(article.body).map((paragraph, index) => (
            <p key={index} className="text-gray-700 mb-6 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>

        {/* Author Card */}
        <div className="bg-gray-50 rounded-lg p-6 mb-12 shadow-sm">
          <div className="flex items-center">
            <div className="h-16 w-16 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xl font-bold mr-4">
              {article.authorName.charAt(0)}
            </div>
            <div>
              <h4 className="text-lg font-semibold">{article.authorName}</h4>
              <p className="text-gray-600 text-sm">{article.authorPosition}</p>
              <p className="text-gray-600 text-sm">
                {article.authorAffiliation}
              </p>
            </div>
          </div>
        </div>

        {/* References Section */}
        <div className="mb-12">
          <div className="flex items-center mb-4">
            <FiBookOpen className="text-indigo-600 mr-2" />
            <h3 className="text-xl font-semibold">References</h3>
          </div>
          <div className="bg-gray-50 rounded-lg p-6">
            <ul className="space-y-2">
              {JSON.parse(article.references).map((reference, index) => (
                <li
                  key={index}
                  className="text-gray-600 text-sm pl-4 border-l-2 border-indigo-200"
                >
                  {reference}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Modal for Fullscreen Image */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="relative max-w-5xl w-full mx-4">
            <button
              className="absolute -top-12 right-0 text-white p-2 hover:bg-gray-800 rounded-full transition-colors"
              onClick={() => setIsModalOpen(false)}
            >
              <FiX size={24} />
            </button>
            <img
              src={article.image}
              alt={article.title}
              className="max-w-full max-h-[85vh] mx-auto object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default DetailsArticlePage;
